import * as React from "react";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { isAxiosError } from "axios";
import { clsx } from "clsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToastDefault } from "@/hooks/use-toast-default";
import { Item } from "@/types/fse";
import { useRenameFileMutation } from "@/api/files";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  item: Item;
}

interface FormInputs {
  name: string;
}

export const RenameModal: React.FC<Props> = ({ item, setIsOpen, isOpen }) => {
  const [error, setError] = useState("");
  const { showToast } = useToastDefault();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormInputs>({
    defaultValues: {
      name: item.name,
    },
  });

  const [renameFile] = useRenameFileMutation();

  const onSubmit = async (payload: FormInputs) => {
    setError("");

    try {
      await renameFile({ fse_id: item.id, name: payload.name });
      setIsOpen(false);
      showToast("Item Renamed", `${item.name} has been renamed to ${payload.name}.`);
    } catch (error) {
      if (isAxiosError(error)) {
        setError(
          (error.response?.data as { err_type?: string })?.err_type === "FileAlreadyExistsError"
            ? "An item with that name already exists."
            : "Failed to rename the item. Please try again.",
        );
      } else {
        setError("Failed to rename the item. Please try again.");
        Sentry.captureException(error);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <form method="post" onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Rename Item</DialogTitle>
            <DialogDescription className="mb-3">Enter a new name for the item.</DialogDescription>
          </DialogHeader>
          <Input
            {...register("name")}
            placeholder="Enter new name"
            className={clsx("my-3", {
              "border-red-500": !!error,
            })}
          />
          {error && <p className="mb-2 text-red-500">{error}</p>}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Renaming..." : "Rename"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
